import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Button, SITE_WIDTH } from './site/ui';
import { IconChevronDown, IconClose, IconMenu } from './icons';
import SiteCredit from './SiteCredit';
import ConsentBanner from './ConsentBanner';
import { isPixelConfigured, reopenConsent, trackPageView } from '../utils/tracking';

/**
 * The three audiences, then pricing. No "Report an Issue" link: the accent
 * button to its right is the same destination and is the one thing on this
 * header that should be impossible to miss — listing it twice spent nav
 * width making the primary action compete with itself.
 */
const navLinks = [
  { to: '/', label: 'Home', end: true },
  { to: '/merchants', label: 'For Merchants' },
  { to: '/couriers', label: 'For Couriers' },
  { to: '/customers', label: 'For Customers' },
  { to: '/pricing', label: 'Pricing' }
];

/**
 * Login and Register each serve two audiences with separate portals and
 * separate signup flows, so one button can't just pick a destination — it
 * asks which. Kept as a small local dropdown rather than two pairs of
 * buttons, which would put four actions in the header.
 */
const AUDIENCE_MENUS = {
  login: [
    { to: '/merchant/login', label: 'For merchants' },
    { to: '/courier/login', label: 'For couriers' }
  ],
  register: [
    { to: '/start-pilot', label: 'For merchants' },
    { to: '/couriers/start-pilot', label: 'For couriers' }
  ]
};

/**
 * Click to open, click outside or Escape to close. Deliberately not
 * hover-driven: the panel hangs below the button with a gap between them, so
 * a mouse travelling down to the first item leaves the trigger and would
 * close the menu before it could be clicked — and hover menus are unusable
 * on touch regardless.
 */
const AudienceMenu = ({ id, label, variant, items, openId, setOpenId, className = '' }) => {
  const open = openId === id;
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (!ref.current?.contains(e.target)) setOpenId(null);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpenId(null);
    };
    // pointerdown, not click: closes as soon as the press lands, so a click
    // on the other trigger isn't swallowed by this one closing first.
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, setOpenId]);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <Button
        variant={variant}
        size="sm"
        className="whitespace-nowrap"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpenId(open ? null : id)}
      >
        {label}
        <IconChevronDown size={15} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
      </Button>
      {open && (
        <div
          role="menu"
          className="animate-rise absolute right-0 top-full z-50 mt-1 w-44 overflow-hidden rounded-xl border border-ink-200 bg-white py-1 shadow-panel"
        >
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              onClick={() => setOpenId(null)}
              className="block px-4 py-2.5 text-sm font-medium text-ink-700 transition-colors hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:bg-ink-100"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const footerLinks = [
  { to: '/report', label: 'Report a delivery issue' },
  { to: '/customers', label: 'For customers' },
  { to: '/merchants', label: 'For merchants' },
  { to: '/couriers', label: 'For couriers' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/verify', label: 'Verify a case' },
  { to: '/contact', label: 'Help & contact' }
];

const Layout = ({ children }) => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  // Which header dropdown is open ('login' | 'register' | null) — one at a
  // time, so opening either closes the other.
  const [openMenu, setOpenMenu] = useState(null);

  // Close the mobile menu and any dropdown on navigation.
  useEffect(() => {
    setMenuOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  // Public pages only (every one of them renders through this Layout): picks
  // up campaign tags from the landing URL and records the page view.
  useEffect(() => {
    trackPageView();
  }, [location.pathname, location.search]);

  return (
    <div className="flex min-h-screen flex-col bg-ink-50">
      <header className="sticky top-0 z-40 border-b border-ink-200 bg-white/90 shrink-0 backdrop-blur-md">
        <div className={`mx-auto w-full ${SITE_WIDTH} px-4 sm:px-6 lg:px-8`}>
          <div className="flex h-20 items-center justify-between gap-4 sm:h-24">
            <Link to="/" className="shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600">
              <img src="/brand.png" alt="ParcelBuddy" className="h-12 w-auto sm:h-14" />
            </Link>

            <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    `relative whitespace-nowrap rounded-lg px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${
                      isActive
                        ? 'font-600 text-ink-900 after:absolute after:inset-x-3 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-accent-500'
                        : 'font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <AudienceMenu
                id="login"
                label="Login"
                variant="secondary"
                items={AUDIENCE_MENUS.login}
                openId={openMenu}
                setOpenId={setOpenMenu}
                className="hidden lg:block"
              />
              <AudienceMenu
                id="register"
                label="Register"
                variant="secondary"
                items={AUDIENCE_MENUS.register}
                openId={openMenu}
                setOpenId={setOpenMenu}
                className="hidden lg:block"
              />
              <Button as={Link} to="/report" variant="accent" size="sm" className="hidden whitespace-nowrap sm:inline-flex">
                Report an Issue
              </Button>
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                className="-mr-1 rounded-lg p-2 text-ink-600 transition-colors hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 lg:hidden"
              >
                {menuOpen ? <IconClose size={20} /> : <IconMenu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="animate-rise border-t border-ink-200 bg-white lg:hidden">
            <nav className={`mx-auto w-full ${SITE_WIDTH} space-y-1 px-4 py-3 sm:px-6`} aria-label="Mobile">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    `block rounded-lg px-3 py-2.5 text-sm transition-colors ${
                      isActive
                        ? 'bg-accent-50 font-600 text-accent-700'
                        : 'font-medium text-ink-700 hover:bg-ink-100'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              {/* Flattened rather than nested dropdowns: there is room here,
                  and a menu inside a menu is poor on touch. */}
              <div className="mt-3 border-t border-ink-100 pt-3">
                <p className="px-3 text-[11px] font-700 uppercase tracking-[0.12em] text-ink-500">
                  Login
                </p>
                {AUDIENCE_MENUS.login.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-700 transition-colors hover:bg-ink-100"
                  >
                    {item.label}
                  </Link>
                ))}
                <p className="mt-2 px-3 text-[11px] font-700 uppercase tracking-[0.12em] text-ink-500">
                  Register
                </p>
                {AUDIENCE_MENUS.register.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-700 transition-colors hover:bg-ink-100"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
              <Button as={Link} to="/report" variant="accent" size="md" className="mt-3 w-full">
                Report an Issue
              </Button>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="shrink-0 bg-brand-900">
        <div className={`mx-auto w-full ${SITE_WIDTH} px-4 py-10 sm:px-6 lg:px-8`}>
          <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
            <div className="max-w-sm">
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="" className="h-10 w-10 rounded-full" />
                <div>
                  <p className="font-display text-base font-800 leading-tight tracking-tight text-white">
                    ParcelBuddy
                  </p>
                  <p className="text-[11px] font-500 uppercase tracking-[0.14em] text-gold-400">
                    Delivery issues. Sorted.
                  </p>
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-brand-100/70">
                ParcelBuddy replaces delivery-issue emails with structured cases — so every report
                reaches the retailer complete, clear and ready to resolve.
              </p>
            </div>

            <nav aria-label="Footer" className="grid grid-cols-1 gap-x-10 gap-y-2 sm:grid-cols-2">
              {footerLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="rounded text-sm text-brand-100/70 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
                >
                  {link.label}
                </Link>
              ))}
              {isPixelConfigured() && (
                <button
                  type="button"
                  onClick={reopenConsent}
                  className="rounded text-left text-sm text-brand-100/70 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
                >
                  Cookie settings
                </button>
              )}
            </nav>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-2 border-t border-white/10 pt-6">
            <p className="text-xs text-brand-100/60">
              © {new Date().getFullYear()} ParcelBuddy. Free for customers — no account required.
            </p>
            <SiteCredit tone="dark" />
          </div>
        </div>
      </footer>

      <ConsentBanner />
    </div>
  );
};

export default Layout;
